import { type FC, useMemo } from 'react'
import { Box, Link as ChakraLink, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import Link from 'next/link'
import Image from 'next/image'
import { ContentContainer } from 'ui'
import type { FooterItem } from 'ecosystem'
import { hrefFromAppPaths } from 'shared-utils'
import { useAppConfigContext } from '#/src/appConfig'
import { COMPANY } from '#/src/custom/seo/company'
import granditudeLogoLight from '../../images/granditude_logo_light.svg'

interface FooterColumn {
  title: string
  links: { label: string; path: string; isExternal?: boolean }[]
}

// Used until footer sections are built in SFA (FOOTER view). Every important page linked
// from every page — the cheapest internal links there are.
const fallbackFooterColumns: FooterColumn[] = [
  {
    title: 'Granditude',
    links: [
      { label: 'What We Do', path: '/what-we-do' },
      { label: 'Working Together', path: '/working-together' },
      { label: 'Partners & Integrations', path: '/partners' },
      { label: 'Blog', path: '/blog' },
      { label: "Let's Talk", path: '/signup' }
    ]
  },
  {
    title: 'Granditude Commerce',
    links: [
      { label: 'Overview', path: '/commerce' },
      { label: 'Storefront', path: '/commerce/storefront' },
      { label: 'Product Information (PIM)', path: '/commerce/pim' },
      { label: 'Order Management', path: '/commerce/order-management' },
      { label: 'Pricing Optimisation', path: '/commerce/pricing' },
      { label: 'Purchase Management', path: '/commerce/purchase-management' },
      { label: 'Warehouse (WMS)', path: '/commerce/wms' },
      { label: 'Aurora', path: '/commerce/aurora' },
      { label: 'Connect & Integrations', path: '/commerce/connect-integrations' }
    ]
  },
  {
    title: 'Integrations',
    links: [
      { label: 'Fortnox', path: '/fortnox-integration' },
      { label: 'nShift', path: '/nshift-integration' },
      { label: 'PostNord', path: '/postnord-integration' },
      { label: 'Nexi', path: '/nexi-integration' },
      { label: 'Elon Group', path: '/elon-group-integration' }
    ]
  }
]

interface CustomFooterProps {
  /** Footer sections built in SFA (FOOTER view, FOOTER_SECTION widgets). */
  footerItems?: FooterItem[]
}

const CustomFooter: FC<CustomFooterProps> = ({ footerItems }) => {
  const appConfig = useAppConfigContext()

  const footerColumns = useMemo<FooterColumn[]>(() => {
    const columns = (footerItems ?? [])
      .map(({ sectionTitle, items }) => ({
        title: sectionTitle,
        links: [...items]
          .sort((a, b) => a.order - b.order)
          .filter((item) => item.text && item.pathObj)
          .map((item) => ({
            label: item.text!,
            path: hrefFromAppPaths(item.pathObj!, appConfig.paths),
            isExternal: item.isExternal
          }))
      }))
      .filter((column) => column.links.length)

    return columns.length ? columns : fallbackFooterColumns
  }, [footerItems, appConfig.paths])

  return (
    <Box
      as="footer"
      w="100%"
      bg="background.dark"
      color="white"
      borderTopWidth={1}
      borderTopColor="border.dark"
      borderTopStyle="solid"
    >
      <ContentContainer py={{ base: 10, md: 16 }}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={{ base: 8, md: 10 }} w="100%">
          {/* Logo + contact info */}
          <VStack spacing={4} align={{ base: 'center', sm: 'flex-start' }}>
            <Link href="/">
              <Image src={granditudeLogoLight} alt="Granditude" height={30} priority />
            </Link>
            <VStack spacing={0} align={{ base: 'center', sm: 'flex-start' }}>
              <ChakraLink
                href={`mailto:${COMPANY.email}`}
                fontSize="0.9rem"
                color="whiteAlpha.800"
                _hover={{ color: 'white' }}
              >
                {COMPANY.email}
              </ChakraLink>
              <Text fontSize="0.9rem" color="whiteAlpha.600">
                +46 (0)70 840 69 51
              </Text>
              <Text as="address" fontStyle="normal" fontSize="0.9rem" color="whiteAlpha.600">
                {COMPANY.streetAddress}, {COMPANY.postalCode} {COMPANY.addressLocality}
              </Text>
              <Text fontSize="0.8rem" color="whiteAlpha.500" pt={2}>
                {COMPANY.legalName} · Org.nr {COMPANY.taxID}
              </Text>
              <ChakraLink
                href={COMPANY.sameAs[0]}
                isExternal
                fontSize="0.9rem"
                color="whiteAlpha.800"
                pt={2}
                _hover={{ color: 'white' }}
              >
                LinkedIn
              </ChakraLink>
            </VStack>
          </VStack>

          {/* Navigation links */}
          {footerColumns.map((column) => (
            <VStack
              key={column.title}
              as="nav"
              aria-label={column.title}
              spacing={2}
              align={{ base: 'center', sm: 'flex-start' }}
            >
              <Text
                fontSize="0.8rem"
                fontWeight={600}
                letterSpacing="0.08em"
                textTransform="uppercase"
              >
                {column.title}
              </Text>
              {column.links.map((link) => (
                <ChakraLink
                  key={link.path}
                  as={link.isExternal ? undefined : Link}
                  href={link.path}
                  isExternal={link.isExternal}
                  fontSize="0.9rem"
                  color="whiteAlpha.600"
                  transition="color 0.2s ease"
                  _hover={{ color: 'white', textDecoration: 'none' }}
                >
                  {link.label}
                </ChakraLink>
              ))}
            </VStack>
          ))}
        </SimpleGrid>
      </ContentContainer>
    </Box>
  )
}

export default CustomFooter
